// ==========================
// src/components/Location.jsx (responsive con layout 2 columnas y acciones adaptativas)
// ==========================
import React, { useState } from 'react';
import {
  Box, Paper, Typography, Accordion, AccordionSummary, AccordionDetails,
  List, ListItem, ListItemText, ListItemIcon, Stack, Button, Divider, Grid, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceIcon from '@mui/icons-material/Place';

// Datos de agencias (puedes editar direcciones y pegar tus URLs de Google Maps)
// Si dejas mapsUrl / embedSrc vacíos, se usarán URLs generadas automáticamente a partir de la dirección.
const LOCATIONS = [
  {
    region: 'Quito',
    agencies: [
      { name: 'Agencia La Prensa', address: 'Av. Prensa y Florida, Quito', mapsUrl: '', embedSrc: '' },
      { name: 'Agencia El Inca', address: 'Av. El Inca y 6 de Diciembre, Quito', mapsUrl: '', embedSrc: '' },
      { name: 'Agencia Amazonas', address: 'Av. Amazonas y Roca, Quito', mapsUrl: '', embedSrc: '' },
      { name: 'Agencia 10 de Agosto', address: 'Av. 10 de Agosto y Alonso de Mercadillo, Quito', mapsUrl: '', embedSrc: '' },
      { name: 'Agencia Calderón', address: 'Miguel de Guarderas y Faustino Carrasco, Quito', mapsUrl: '', embedSrc: '' },
      { name: 'Agencia El Pintado', address: 'Av. Mariscal Sucre y Antonio Jaramillo (Sur de Quito)', mapsUrl: '', embedSrc: '' },
    ],
  },
  {
    region: 'Valle de los Chillos',
    agencies: [
      { name: 'Agencia El Triángulo', address: 'Av. Gral. Rumiñahui y Av. Ilálo, Valle de los Chillos', mapsUrl: '', embedSrc: '' },
    ],
  },
  {
    region: 'Sierra Norte',
    agencies: [
      { name: 'Agencia Ibarra', address: 'Simón Bolivar y Pedro Moncayo, Ibarra', mapsUrl: '', embedSrc: '' },
      { name: 'Agencia Otavalo', address: 'Modesto Jaramillo y Juan Montalvo, Otavalo', mapsUrl: '', embedSrc: '' },
    ],
  },
  {
    region: 'Portoviejo',
    agencies: [
      { name: 'Agencia Portoviejo', address: 'Calle Quito y Ricaurte esquina, Portoviejo', mapsUrl: '', embedSrc: '' },
    ],
  },
];

// Generadores de URL por defecto (útiles si todavía no pegas tus links)
const toMapsLink = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
const toEmbedSrc  = (q) => `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;

export default function Location(){
  const [embedSrc, setEmbedSrc] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');

  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const handlePreview = (agency) => {
    const src = agency.embedSrc || toEmbedSrc(agency.address);
    setEmbedSrc(src);
    setSelectedLabel(`${agency.name} — ${agency.address}`);
  };

  return (
    <Paper elevation={0} sx={{ p:3, border: '1px solid #eee' }}>
      <Typography sx={{ mb: 2 }}>Encuentra tu agencia más cercana. Abre el mapa o previsualiza aquí mismo.</Typography>

      <Grid container spacing={2} alignItems="flex-start">
        {/* Columna de listado */}
        <Grid item xs={12} md={6}>
          {LOCATIONS.map((group, idx) => (
            <Accordion key={group.region} defaultExpanded={idx === 0} disableGutters sx={{ border: '1px solid #eee', mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}> 
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{group.region}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense disablePadding>
                  {group.agencies.map((a) => {
                    const mapsHref = a.mapsUrl || toMapsLink(a.address);
                    return (
                      <Box key={a.name}>
                        {/* En escritorio mostramos acciones a la derecha, en móvil debajo */}
                        <ListItem
                          alignItems="flex-start"
                          secondaryAction={mdUp ? (
                            <Stack direction="row" spacing={1}>
                              <Button size="small" variant="outlined" onClick={() => handlePreview(a)}>Previsualizar</Button>
                              <Button size="small" variant="contained" component="a" href={mapsHref} target="_blank" rel="noopener noreferrer">Abrir en Maps</Button>
                            </Stack>
                          ) : undefined}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}><PlaceIcon /></ListItemIcon>
                          <ListItemText primary={a.name} secondary={a.address} />
                        </ListItem>
                        {/* Acciones para móvil (debajo) */}
                        {!mdUp && (
                          <Box sx={{ px: 2, pb: 1.5 }}>
                            <Stack direction={{ xs:'column', sm:'row' }} spacing={1}>
                              <Button variant="outlined" onClick={() => handlePreview(a)} sx={{ width: { xs:'100%', sm:'auto' } }}>Previsualizar</Button>
                              <Button variant="contained" component="a" href={mapsHref} target="_blank" rel="noopener noreferrer" sx={{ width: { xs:'100%', sm:'auto' } }}>Abrir en Maps</Button>
                            </Stack>
                          </Box>
                        )}
                        <Divider component="li" />
                      </Box>
                    );
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Grid>

        {/* Columna de mapa (sticky en desktop) */}
        <Grid item xs={12} md={6}>
          <Box sx={{ position: { md:'sticky' }, top: { md: 96 }, borderRadius: 2, overflow: 'hidden', border: '1px solid #ddd' }}>
            {embedSrc ? (
              <>
                <Box sx={{ p:1, bgcolor: '#faf7ef' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{selectedLabel}</Typography>
                </Box>
                <Box sx={{ height: { xs: 260, sm: 320, md: 420, lg: 480 } }}>
                  <iframe
                    title="mapa-agencia"
                    src={embedSrc}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </Box>
              </>
            ) : (
              <Box sx={{ p:3, textAlign:'center', color:'text.secondary' }}>
                Selecciona una agencia y presiona <strong>Previsualizar</strong> para ver el mapa aquí.
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}